import { balanced } from 'balanced-match'

const escSlash = '\0SLASH' + Math.random() + '\0'
const escOpen = '\0OPEN' + Math.random() + '\0'
const escClose = '\0CLOSE' + Math.random() + '\0'
const escComma = '\0COMMA' + Math.random() + '\0'
const escPeriod = '\0PERIOD' + Math.random() + '\0'
const escSlashPattern = new RegExp(escSlash, 'g')
const escOpenPattern = new RegExp(escOpen, 'g')
const escClosePattern = new RegExp(escClose, 'g')
const escCommaPattern = new RegExp(escComma, 'g')
const escPeriodPattern = new RegExp(escPeriod, 'g')
const slashPattern = /\\\\/g
const openPattern = /\\{/g
const closePattern = /\\}/g
const commaPattern = /\\,/g
const periodPattern = /\\\./g

export const EXPANSION_MAX = 100_000

// `EXPANSION_MAX` caps the *number* of expansions, but not their length. An
// input like `'{a,b}'.repeat(1500)` stays under that count - its output is
// truncated to 100k results - while making every result ~1500 characters
// long. The result set, and the intermediate arrays built while combining
// brace sets, then grow large enough to exhaust memory and crash the process
// (CVE-2026-14257). `EXPANSION_MAX_LENGTH` bounds the total number of
// characters the accumulator may hold at any point, so memory stays flat no
// matter how many brace groups are chained. The limit sits well above any
// realistic expansion (100k results hitting `EXPANSION_MAX` measure ~1M
// characters) so legitimate input is unaffected.
export const EXPANSION_MAX_LENGTH = 4_000_000

// `expand_` recurses once per level of brace *nesting* - both when expanding a
// set's comma members and when re-wrapping a set whose body is a single part.
// The CVE-2026-14257 fix made the *tail* iterative (recursion on `m.post`, one
// level per chained group), which left nesting depth unbounded: about 3,100
// levels of `{{{...a,b...}}}` - only ~6KB of input - exhausted the native stack
// and crashed the process. `EXPANSION_MAX_DEPTH` bounds how deep the parser
// will follow nesting. It sits far above any realistic pattern and well below
// the depth at which the stack runs out.
export const EXPANSION_MAX_DEPTH = 1_000

// Bash keeps a quirk where a brace group followed by a comma set still expands
// (`{a},b}`). The parser implements it by rewriting the string and restarting
// the scan, absorbing one `}` per pass. `n` trailing braces therefore cost `n`
// full passes over a string that itself grows by one `escClose` sentinel each
// time - quadratic in `n`, with a ~26x constant from the sentinel's length.
// 128KB of `'{a}' + '}'.repeat(n) + ',z}'` blocked the event loop for 27
// seconds to produce two results. `EXPANSION_MAX_REWRITES` bounds how many
// times the scan may restart. Real `{a},b}` input needs a handful.
export const EXPANSION_MAX_REWRITES = 1_000

function numeric(str: string) {
  return !isNaN(str as any) ? parseInt(str, 10) : str.charCodeAt(0)
}

function escapeBraces(str: string) {
  return str
    .replace(slashPattern, escSlash)
    .replace(openPattern, escOpen)
    .replace(closePattern, escClose)
    .replace(commaPattern, escComma)
    .replace(periodPattern, escPeriod)
}

function unescapeBraces(str: string) {
  return str
    .replace(escSlashPattern, '\\')
    .replace(escOpenPattern, '{')
    .replace(escClosePattern, '}')
    .replace(escCommaPattern, ',')
    .replace(escPeriodPattern, '.')
}

// Like `target.push(...items)` but doesn't overflow the stack
function pushAll(target: string[], items: string[]) {
  for (let i = 0; i < items.length; i++) {
    target.push(items[i] as string)
  }
}

/**
 * Basically just str.split(","), but handling cases
 * where we have nested braced sections, which should be
 * treated as individual members, like {a,{b,c},d}
 */
function parseCommaParts(str: string) {
  const parts: string[] = []

  // Walk the brace groups iteratively. Recursing on `post` once per group let a
  // chain of them exhaust the stack - the parsing-side counterpart to
  // the `expand_` overflow fixed for CVE-2026-14257, and not something `max` or
  // `maxLength` can bound, since it happens before expansion.
  //
  // The part the next chunk continues
  let carry = ''

  for (;;) {
    const m = balanced('{', '}', str)

    if (!m) {
      const tail = str.split(',')
      tail[0] = carry + (tail[0] as string)
      pushAll(parts, tail)
      return parts
    }

    const { pre, body, post } = m
    const p = pre.split(',')
    p[0] = carry + (p[0] as string)
    p[p.length - 1] += '{' + body + '}'

    if (!post.length) {
      pushAll(parts, p)
      return parts
    }

    carry = p.pop() as string
    pushAll(parts, p)
    str = post
  }
}

export type BraceExpansionOptions = {
  max?: number
  maxLength?: number
  maxDepth?: number
  maxRewrites?: number
}

export function expand(str: string, options: BraceExpansionOptions = {}) {
  if (!str) {
    return []
  }

  const {
    max = EXPANSION_MAX,
    maxLength = EXPANSION_MAX_LENGTH,
    maxDepth = EXPANSION_MAX_DEPTH,
    maxRewrites = EXPANSION_MAX_REWRITES,
  } = options

  // I don't know why Bash 4.3 does this, but it does.
  // Anything starting with {} will have the first two bytes preserved
  // but *only* at the top level, so {},a}b will not expand to anything,
  // but a{},b}c will be expanded to [a}c,abc].
  // One could argue that this is a bug in Bash, but since the goal of
  // this module is to match Bash's rules, we escape a leading {}
  if (str.slice(0, 2) === '{}') {
    str = '\\{\\}' + str.slice(2)
  }

  return expand_(
    escapeBraces(str),
    max,
    maxLength,
    maxDepth,
    0,
    maxRewrites,
    true,
  ).map(unescapeBraces)
}

function embrace(str: string) {
  return '{' + str + '}'
}

function isPadded(el: string) {
  return /^-?0\d/.test(el)
}

function lte(i: number, y: number) {
  return i <= y
}

function gte(i: number, y: number) {
  return i >= y
}

// Build `{ acc[a] + pre + values[v] }` for every combination, capping the
// number of results at `max` and the total number of characters at `maxLength`.
// This is the one place output grows, so bounding it here keeps the single
// accumulator - and therefore memory - flat regardless of how many brace groups
// are combined (CVE-2026-14257).
function combine(
  acc: string[],
  pre: string,
  values: string[],
  max: number,
  maxLength: number,
  dropEmpties: boolean,
): string[] {
  const out: string[] = []
  let length = 0
  for (let a = 0; a < acc.length; a++) {
    for (let v = 0; v < values.length; v++) {
      if (out.length >= max) return out
      const expansion = (acc[a] as string) + pre + values[v]
      // Bash drops empty results at the top level. Skip them before they count
      // against `max`, so `max` bounds the number of *kept* results.
      if (dropEmpties && !expansion) continue
      if (length + expansion.length > maxLength) return out
      out.push(expansion)
      length += expansion.length
    }
  }
  return out
}

// The expansion values of a single numeric (`1..5`) or alphabetic (`a..e..2`)
// sequence body.
function expandSequence(
  body: string,
  isAlphaSequence: boolean,
  max: number,
  maxLength: number,
): string[] {
  const n = body.split(/\.\./)
  const N: string[] = []
  // A sequence body always splits into two or three parts, but the compiler
  // can't know that.
  /* c8 ignore start */
  if (n[0] === undefined || n[1] === undefined) {
    return N
  }
  /* c8 ignore stop */
  const x = numeric(n[0])
  const y = numeric(n[1])
  const width = Math.max(n[0].length, n[1].length)
  let incr =
    n.length === 3 && n[2] !== undefined ?
      Math.max(Math.abs(numeric(n[2])), 1)
    : 1
  let test = lte
  const reverse = y < x
  if (reverse) {
    incr *= -1
    test = gte
  }
  const pad = n.some(isPadded)

  let length = 0
  for (let i = x; test(i, y) && N.length < max; i += incr) {
    let c
    if (isAlphaSequence) {
      c = String.fromCharCode(i)
      if (c === '\\') {
        c = ''
      }
    } else {
      c = String(i)
      if (pad) {
        const need = width - c.length
        if (need > 0) {
          const z = new Array(need + 1).join('0')
          if (i < 0) {
            c = '-' + z + c.slice(1)
          } else {
            c = z + c
          }
        }
      }
    }
    if (length + c.length > maxLength) break
    N.push(c)
    length += c.length
  }
  return N
}

function expand_(
  str: string,
  max: number,
  maxLength: number,
  maxDepth: number,
  depth: number,
  maxRewrites: number,
  isTop: boolean,
): string[] {
  // Too deeply nested to keep following: treat the rest as literal, the same
  // way a group that cannot expand is already handled. Truncating rather than
  // throwing keeps `expand` total, matching `max` and `maxLength`.
  if (depth > maxDepth) {
    return [str]
  }

  // Consume the string's top-level brace groups left to right, threading a
  // running set of combined prefixes (`acc`). Expanding the tail iteratively -
  // rather than recursing on `m.post` once per group - keeps the native stack
  // depth constant, so deeply chained input (`'{a,b}'.repeat(3000)`) can no
  // longer overflow the stack, and leaves a single accumulator whose size
  // `maxLength` bounds directly (CVE-2026-14257).
  let acc: string[] = ['']

  // Bash drops empty results, but only when the *first* top-level group is a
  // comma set - a sequence like `{a..\}` may legitimately yield ''. The drop
  // is on the final strings, so it is applied to whichever `combine` produces
  // them (the one with no brace set left in the tail).
  // How many times the `{a},b}` rewrite below has restarted the scan. Each pass
  // re-reads the whole string, so leaving this unbounded is quadratic.
  let rewrites = 0
  let dropEmpties = false
  let firstGroup = true

  for (;;) {
    const m = balanced('{', '}', str)

    // No brace set left: the rest of the string is literal.
    if (!m) {
      return combine(acc, str, [''], max, maxLength, dropEmpties)
    }

    // no need to expand pre, since it is guaranteed to be free of brace-sets
    const pre = m.pre

    if (/\$$/.test(pre)) {
      acc = combine(
        acc,
        pre + '{' + m.body + '}',
        [''],
        max,
        maxLength,
        dropEmpties && !m.post.length,
      )
      firstGroup = false
      if (!m.post.length) break
      str = m.post
      continue
    }

    const isNumericSequence = /^-?\d+\.\.-?\d+(?:\.\.-?\d+)?$/.test(m.body)
    const isAlphaSequence = /^[a-zA-Z]\.\.[a-zA-Z](?:\.\.-?\d+)?$/.test(
      m.body,
    )
    const isSequence = isNumericSequence || isAlphaSequence
    const isOptions = m.body.indexOf(',') >= 0
    if (!isSequence && !isOptions) {
      // {a},b}
      if (rewrites < maxRewrites && m.post.match(/,(?!,).*\}/)) {
        rewrites++
        str = m.pre + '{' + m.body + escClose + m.post
        isTop = true
        continue
      }
      // Nothing here expands, so the whole remaining string is literal.
      return combine(
        acc,
        pre + '{' + m.body + '}' + m.post,
        [''],
        max,
        maxLength,
        dropEmpties,
      )
    }

    if (firstGroup) {
      dropEmpties = isTop && !isSequence
      firstGroup = false
    }

    let values: string[]
    if (isSequence) {
      values = expandSequence(m.body, isAlphaSequence, max, maxLength)
    } else {
      let n = parseCommaParts(m.body)
      if (n.length === 1 && n[0] !== undefined) {
        // x{{a,b}}y ==> x{a}y x{b}y
        n = expand_(
          n[0],
          max,
          maxLength,
          maxDepth,
          depth + 1,
          maxRewrites,
          false,
        ).map(embrace)
        //XXX is this necessary? Can't seem to hit it in tests.
        /* c8 ignore start */
        if (n.length === 1) {
          acc = combine(
            acc,
            pre + n[0],
            [''],
            max,
            maxLength,
            dropEmpties && !m.post.length,
          )
          if (!m.post.length) break
          str = m.post
          continue
        }
        /* c8 ignore stop */
      }

      // Values that `combine` is going to drop as empty produce no result, so
      // they must not count against `max` - otherwise `{a,,b}` with `max: 2`
      // would stop at `['a', '']` and yield one result instead of two. Skipping
      // them outright keeps `values` bounded while leaving `max` a bound on
      // *kept* results.
      let dropsEmpties = dropEmpties && !m.post.length && !pre
      for (let d = 0; dropsEmpties && d < acc.length; d++) {
        if (acc[d]) {
          dropsEmpties = false
        }
      }

      values = []
      let valuesLength = 0
      outer: for (let j = 0; j < n.length; j++) {
        const expanded = expand_(
          n[j] as string,
          max,
          maxLength,
          maxDepth,
          depth + 1,
          maxRewrites,
          false,
        )
        for (let k = 0; k < expanded.length; k++) {
          const v = expanded[k] as string
          if (dropsEmpties && !v) continue
          if (
            values.length >= max ||
            valuesLength + v.length > maxLength
          ) {
            break outer
          }
          values.push(v)
          valuesLength += v.length
        }
      }
    }

    acc = combine(
      acc,
      pre,
      values,
      max,
      maxLength,
      dropEmpties && !m.post.length,
    )
    if (!m.post.length) break
    str = m.post
  }

  return acc
}
